import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IconButton, Stack, type SxProps, Tooltip } from "@mui/material";
import { copyToClipboard } from "@src/appV2/redesign/utils/copyToClipboard";
import { Text } from "@src/lib/ui-react";
import { useState } from "react";

// TODO: export TextVariant from @src/lib/ui-react
type TextVariant =
  | "caption"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "body1"
  | "subtitle1"
  | "subtitle2"
  | "body2";
export interface CopyableTextProps {
  value: string;
  copyableText?: string;
  onCopyClick?: () => void;
  textVariant?: TextVariant;
  rootStackSx?: SxProps;
  showText?: boolean;
  // Accessible name for the copy IconButton. The button is icon-only, so without
  // this it would be announced as a generic "button". Defaults to "Copy" /
  // "Copied" to match the tooltip text.
  copyButtonAriaLabel?: string;
}

export function CopyableText(props: CopyableTextProps): JSX.Element {
  const {
    rootStackSx,
    copyableText,
    value,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onCopyClick = () => {},
    textVariant = "body1",
    showText = true,
    copyButtonAriaLabel,
  } = props;
  const [isCopied, setIsCopied] = useState<boolean>(false);

  async function handleCopy(): Promise<void> {
    onCopyClick();
    await copyToClipboard(copyableText ?? value);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  }

  const buttonAriaLabel = copyButtonAriaLabel ?? (isCopied ? "Copied" : "Copy");

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={rootStackSx}>
      {showText && <Text variant={textVariant}>{value}</Text>}
      <Tooltip title={isCopied ? "Copied!" : "Copy"}>
        <IconButton
          aria-label={buttonAriaLabel}
          edge="end"
          sx={{ padding: 0 }}
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => {
            void handleCopy();
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
